import { ENGAGED_VISIT_THRESHOLDS } from "@repo/shared";
import { useEffect } from "react";

import { trackEngagedVisit } from "./events";

import type { EngagedReason } from "../visits/beacon";

/**
 * Visita comprometida (`engaged_visit`): en la página de un curso, la persona estuvo ACTIVA al
 * menos `activeMs` (30 s con la pestaña visible: mirar el video cuenta, aunque no toque nada) o
 * recorrió al menos `scrollRatio` (50 %) de la página scrolleando. Se dispara UNA vez por vista de
 * página: volver a abrir el curso (otra vista) puede generar otra.
 *
 * Los umbrales viven en `ENGAGED_VISIT_THRESHOLDS` (`@repo/shared`): el admin los muestra y la API
 * los documenta con el mismo valor.
 */

export type EngagementThresholds = { activeMs: number; scrollRatio: number };

/** Más que esto entre dos ticks es una pestaña dormida o un celular bloqueado: no se suma. */
export const MAX_TICK_MS = 2_000;

export function createEngagementTracker(
  onEngaged: (reason: EngagedReason) => void,
  thresholds: EngagementThresholds = ENGAGED_VISIT_THRESHOLDS,
) {
  let activeMs = 0;
  let fired = false;

  const fire = (reason: EngagedReason) => {
    if (fired) return;
    fired = true;
    onEngaged(reason);
  };

  return {
    /** Pasaron `elapsedMs` desde el tick anterior; sólo cuentan con la pestaña visible. */
    tick(elapsedMs: number, visible: boolean) {
      if (fired || !visible || !(elapsedMs > 0)) return;
      activeMs += Math.min(elapsedMs, MAX_TICK_MS);
      if (activeMs >= thresholds.activeMs) fire("time");
    },
    /**
     * Cuánto de la página se ve recorrido (0–1). Sólo vale si la persona scrolleó (`scrolled`): en
     * una página corta que entra entera en la pantalla, verla no es «comprometerse».
     */
    scroll(ratio: number, scrolled: boolean) {
      if (fired || !scrolled) return;
      if (ratio >= thresholds.scrollRatio) fire("scroll");
    },
    get fired() {
      return fired;
    },
    get activeMs() {
      return activeMs;
    },
  };
}

/** Fracción de la página ya recorrida: lo de arriba más lo que se ve, sobre el alto total. */
export function scrollRatioOf(input: {
  scrollY: number;
  innerHeight: number;
  scrollHeight: number;
}): number {
  if (!(input.scrollHeight > 0)) return 0;
  return Math.min(1, Math.max(0, (input.scrollY + input.innerHeight) / input.scrollHeight));
}

const TICK_MS = 1_000;

/**
 * Mide la visita comprometida de la página actual. Se reinicia si cambia `viewKey` (otro curso u
 * otra vista). Sin `viewKey`, no mide. Nunca tira: medir no puede romper la página.
 */
export function useEngagedVisit(
  viewKey: string | null | undefined,
  onEngaged: (reason: EngagedReason) => void = (reason) => trackEngagedVisit(reason, viewKey),
) {
  useEffect(() => {
    if (!viewKey || typeof window === "undefined") return;
    let tracker: ReturnType<typeof createEngagementTracker>;
    try {
      tracker = createEngagementTracker(onEngaged);
    } catch {
      return;
    }

    let last = Date.now();
    const interval = window.setInterval(() => {
      const now = Date.now();
      const elapsed = now - last;
      last = now;
      try {
        tracker.tick(elapsed, document.visibilityState === "visible");
        if (tracker.fired) window.clearInterval(interval);
      } catch {
        // La medición nunca rompe la página.
      }
    }, TICK_MS);

    const onScroll = () => {
      try {
        const root = document.documentElement;
        tracker.scroll(
          scrollRatioOf({
            scrollY: window.scrollY,
            innerHeight: window.innerHeight,
            scrollHeight: root.scrollHeight,
          }),
          window.scrollY > 0,
        );
        if (tracker.fired) window.removeEventListener("scroll", onScroll);
      } catch {
        // La medición nunca rompe la página.
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("scroll", onScroll);
    };
    // `onEngaged` cambia en cada render: la medición se reinicia sólo con otra vista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey]);
}
